/**
 * Subscription utilities for determining feature access
 */

// Subscription hierarchy (ascending order)
const SUBSCRIPTION_HIERARCHY = ['essentials', 'advanced', 'premium', 'enterprise'];

const SUBSCRIPTION_ALIASES = {
  professional: 'premium',
};

// Page access logic:
// 1) Product first: company must have the product enabled.
// 2) Plan second: used only as a minimum floor.
// Feature limits (Slack/Teams/etc.) are validated separately on save.
const PAGE_ACCESS_RULES = {
  tag_monitor_config: {
    productFlag: 'tag_monitor',
    planField: 'tag_monitor_subscription_model',
    minPlan: 'essentials',
  },
  tag_monitor_dashboard: {
    productFlag: 'tag_monitor',
    planField: 'tag_monitor_subscription_model',
    minPlan: 'essentials',
  },
  datalayer_guard_config: {
    productFlag: 'datalayer_guard',
    planField: 'datalayer_guard_subscription_model',
    minPlan: 'essentials',
  },
  datalayer_guard_dashboard: {
    productFlag: 'datalayer_guard',
    planField: 'datalayer_guard_subscription_model',
    minPlan: 'essentials',
  },
  sitespeed_monitor_config: {
    productFlag: 'sitespeed_monitor',
  },
};

export const normalizeSubscriptionPlan = (plan) => {
  const normalized = String(plan || '').trim().toLowerCase();
  return SUBSCRIPTION_ALIASES[normalized] || normalized;
};

/**
 * Get the minimum required subscription plan for a Tag Monitor feature
 * @param {string} feature - Feature name
 * @returns {string} - Minimum subscription plan name
 */
export const getRequiredTagMonitorPlan = (feature) => {
  const featureMap = {
    'client_side_monitoring': 'Essentials',
    'error_monitoring': 'Essentials',
    'email_notif': 'Essentials',
    'server_side_monitoring': 'Essentials',
    'cloud_run_monitoring': 'Advanced',
    'tag_inactivity': 'Advanced',
    'slack_notif': 'Advanced',
    'teams_notif': 'Professional',
  };
  
  return featureMap[feature] || 'Advanced';
};

/**
 * Get the minimum required subscription plan for a DataLayer Guard feature
 * @param {string} feature - Feature name
 * @returns {string} - Minimum subscription plan name
 */
export const getRequiredDataLayerGuardPlan = (feature) => {
  const featureMap = {
    'email_notif': 'Essentials',
    'slack_notif': 'Advanced',
    'teams_notif': 'Professional',
  };
  
  return featureMap[feature] || 'Advanced';
};

/**
 * Check if a subscription plan includes a specific feature
 * @param {string} currentPlan - Current subscription plan (essentials, advanced, premium/professional, enterprise)
 * @param {string} requiredPlan - Required subscription plan for the feature
 * @returns {boolean} - Whether the current plan includes the feature
 */
export const hasFeatureAccess = (currentPlan, requiredPlan) => {
  const currentIndex = SUBSCRIPTION_HIERARCHY.indexOf(normalizeSubscriptionPlan(currentPlan));
  const requiredIndex = SUBSCRIPTION_HIERARCHY.indexOf(normalizeSubscriptionPlan(requiredPlan));
  
  return currentIndex >= requiredIndex;
};

export const canAccessPage = ({ pageKey, companyInfo }) => {
  const rule = PAGE_ACCESS_RULES[pageKey];
  if (!rule) return true;

  if (rule.productFlag && !companyInfo?.[rule.productFlag]) {
    return false;
  }

  if (rule.planField && rule.minPlan) {
    const currentPlan = companyInfo?.[rule.planField];
    if (!hasFeatureAccess(currentPlan, rule.minPlan)) {
      return false;
    }
  }

  return true;
};
